import { cycleWeekIndex, dateKey } from './cycle'
import { encodeCompletionId } from './completion-id'
import { isUsableTaskId } from './task-id'
import type { CustomChore, Plan, TaskTemplate } from '../types'

/**
 * The rotation engine: which chores a given date asks for.
 *
 * Pure, and the plan arrives as an argument rather than an import — the whole
 * point of the split in `docs/architecture-review.md` §1.3. The literal in
 * `src/data/default-plan.ts` is no longer the only source: `buildPlan` below
 * folds the household's own chores into it, and this function cannot tell the
 * difference. That was the point of the seam.
 *
 * Total by construction: `Plan.weekly` is a seven-tuple and
 * `Plan.saturdayRotation` a four-tuple, so there is no weekday and no cycle
 * week for which the compiler will accept a plan with nothing to return.
 */
export function tasksForDate(date: Date, plan: Plan): TaskTemplate[] {
  const weekday = date.getDay()
  const week = cycleWeekIndex(date)
  const tasks = [...plan.daily, ...plan.weekly[weekday]]

  if (weekday === 6) tasks.push(...plan.saturdayRotation[week])
  if (weekday === 0 && week === 3) tasks.push(...plan.monthlySunday)

  const once = plan.oneOff[dateKey(date)]
  if (once) tasks.push(...once)

  return tasks
}

/** The mutable twin of `WeekdayTable`, while a plan is being assembled. */
type WeekdaySlots = [
  TaskTemplate[],
  TaskTemplate[],
  TaskTemplate[],
  TaskTemplate[],
  TaskTemplate[],
  TaskTemplate[],
  TaskTemplate[],
]

/** The mutable twin of `CycleTable`. */
type CycleSlots = [TaskTemplate[], TaskTemplate[], TaskTemplate[], TaskTemplate[]]

function inRange(value: number, upper: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= upper
}

/**
 * Fold the household's own chores into a plan.
 *
 * This is the whole of "the plan is data" as it currently exists: the built-in
 * rotation stays a literal, and everything a parent adds from the phone is a
 * synced row that lands in the same slots. `tasksForDate`, `dayProgress`, the
 * day list and the month grid all read the result and none of them knows which
 * chore came from where.
 *
 * `base` is never mutated — the caller's literal is a module-level constant
 * shared by every render, and appending to it in place would grow the plan by
 * one copy of every custom chore each time this ran.
 *
 * **A chore this function cannot place is dropped, not repaired.** `schedule`,
 * `weekday` and `cycleWeek` arrive from Postgres and from `localStorage`, where
 * a value out of range is possible and a crash is not acceptable: the
 * alternative to skipping is either a thrown error inside a render or a chore
 * silently appearing on the wrong day, and an invisible chore is the only one
 * of the three a user can diagnose. `isUsableTaskId` is part of the same rule —
 * see the note there about `encodeCompletionId` throwing.
 */
export function buildPlan(base: Plan, chores: readonly CustomChore[]): Plan {
  if (!chores.length) return base

  const daily = [...base.daily]
  const monthlySunday = [...base.monthlySunday]
  const weekly: WeekdaySlots = [
    [...base.weekly[0]],
    [...base.weekly[1]],
    [...base.weekly[2]],
    [...base.weekly[3]],
    [...base.weekly[4]],
    [...base.weekly[5]],
    [...base.weekly[6]],
  ]
  const saturdayRotation: CycleSlots = [
    [...base.saturdayRotation[0]],
    [...base.saturdayRotation[1]],
    [...base.saturdayRotation[2]],
    [...base.saturdayRotation[3]],
  ]
  const oneOff: Record<string, TaskTemplate[]> = {}
  for (const [date, tasks] of Object.entries(base.oneOff)) oneOff[date] = [...tasks]

  for (const chore of chores) {
    if (!isUsableTaskId(chore.id)) continue

    const template: TaskTemplate = {
      id: chore.id,
      label: chore.label,
      emoji: chore.emoji,
      category: chore.category,
      assigneeId: chore.assigneeId,
    }

    switch (chore.schedule) {
      case 'daily':
        daily.push(template)
        break
      case 'weekday':
        if (inRange(chore.weekday, 6)) weekly[chore.weekday].push(template)
        break
      case 'saturday-rotation':
        if (inRange(chore.cycleWeek, 3)) saturdayRotation[chore.cycleWeek].push(template)
        break
      case 'monthly-sunday':
        monthlySunday.push(template)
        break
      case 'once':
        if (chore.onDate) (oneOff[chore.onDate] ??= []).push(template)
        break
      default:
        // An unknown kind is a row written by a newer version of the app, or a
        // hand-edited one. Showing it every day would be worse than not showing
        // it at all.
        break
    }
  }

  return { daily, weekly, saturdayRotation, monthlySunday, oneOff }
}

/**
 * The chores one member is actually looking at.
 *
 * Assignment is a filter over the day's plan, not a second plan: a chore with no
 * assignee belongs to the whole house and appears in everybody's list, where
 * each of them gets their own completion row for it — which is what the member
 * component of the completion id was always for. A chore assigned to somebody
 * appears in exactly one list.
 *
 * It lives here, beside `dayProgress`, because three views have to agree: the
 * day list, the header orb and every cell of the month grid. Filtering in two of
 * them and not the third is how a day reads 3/5 in one place and 3/3 in another,
 * and that is the kind of disagreement people report as a sync bug.
 */
export function tasksForMember(
  tasks: readonly TaskTemplate[],
  memberId: string,
): TaskTemplate[] {
  return tasks.filter((task) => !task.assigneeId || task.assigneeId === memberId)
}

export type DayProgress = {
  done: number
  total: number
  /** 0–100, rounded. A day with no chores is 0, not `NaN`. */
  percent: number
}

/**
 * How much of a day's plan one member has ticked.
 *
 * The `done` set holds completion ids, so the member has to be named here: two
 * children who share a chore have two rows for it, and counting without the
 * member would show each of them the other's ticks (`CLAUDE.md` §1).
 *
 * It lives beside the engine because three views want the same number — the
 * header orb, the day list and every cell of the month grid — and the version
 * that was inlined in each of them was the same `encodeCompletionId` filter
 * written out three times.
 */
export function dayProgress(
  tasks: readonly TaskTemplate[],
  taskDate: string,
  memberId: string,
  done: ReadonlySet<string>,
): DayProgress {
  const count = tasks.filter((task) =>
    done.has(encodeCompletionId({ taskDate, taskId: task.id, memberId })),
  ).length

  return {
    done: count,
    total: tasks.length,
    percent: tasks.length ? Math.round((count / tasks.length) * 100) : 0,
  }
}

/** Whether one chore is ticked for a member on a day. */
export function isTicked(
  taskId: string,
  taskDate: string,
  memberId: string,
  done: ReadonlySet<string>,
) {
  return done.has(encodeCompletionId({ taskDate, taskId, memberId }))
}
